import { useState } from "react";

import { Wordmark } from "../components/Wordmark.js";
import { api, ApiError } from "../lib/api.js";
import { passkeysSupported, signInWithPasskey, wasCancelled } from "../lib/passkey.js";

export function SignIn(): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const expired = new URLSearchParams(window.location.search).get("signin") === "expired";

  async function send(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    await api.requestLink(email);
    setBusy(false);
    setSent(true);
  }

  async function withPasskey(): Promise<void> {
    setPasskeyBusy(true);
    setPasskeyError(null);
    try {
      await signInWithPasskey();
      // A full load rather than a state change: the session gate, the service
      // worker and every screen start from a signed-in first request.
      window.location.assign("/");
      return;
    } catch (error) {
      if (!wasCancelled(error)) {
        setPasskeyError(
          error instanceof ApiError ? error.message : "That didn't work. Use an email link instead.",
        );
      }
    }
    setPasskeyBusy(false);
  }

  return (
    <div className="signin">
      {/*
        * The one screen with nothing else to say. It was already spelling the
        * name out in letterspaced caps; the mark does that job properly.
        */}
      <Wordmark height={30} className="signin__mark" />

      {sent ? (
        <>
          <h1>Check your email.</h1>
          <p className="signin__sub">The link works once and expires in 15 minutes.</p>
          <button type="button" className="linkish" onClick={() => setSent(false)}>
            Use a different address
          </button>
        </>
      ) : (
        <>
          <h1>Sign in.</h1>
          {expired && <p className="error">That link has expired. Send yourself another.</p>}

          {/*
            * Beside the email link, never instead of it. A passkey is added
            * from settings, so the first time on any device is always the link.
            */}
          {passkeysSupported() && (
            <div className="signin__passkey">
              <button
                type="button"
                className="button-ink"
                disabled={passkeyBusy}
                onClick={() => void withPasskey()}
              >
                {passkeyBusy ? "Waiting…" : "Sign in with Face ID or passkey"}
              </button>
              {passkeyError !== null && <p className="error">{passkeyError}</p>}
              <p className="signin__or">or get a link by email</p>
            </div>
          )}

          <form onSubmit={(e) => void send(e)}>
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              placeholder="you@example.com"
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" className="button-ink" disabled={busy}>
              {busy ? "Sending…" : "Send me a link"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
